import { StepPage } from "features/subscription-process/navigation/Steps/StepPage";
import { useCallback, useEffect, useMemo } from "react";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { Sizes } from "components/forms/NumberSelector";
import { BorrowerProfilingInsuredSection } from "./components/borrower/BorrowerProfilingInsuredSection";
import { BorrowerProfilingLoanSection } from "./components/borrower/BorrowerProfilingLoanSection";
import { BorrowerProfilingProjectSection } from "./components/borrower/BorrowerProfilingProjectSection";
import { BorrowerProjectDocumentsSection } from "../components/BorrowerProjectDocumentsSection";
import { useNavigate, useParams } from "react-router-dom";
import { MainBorrowerProfilingProvider } from "./contexts/main-borrower-profiling/MainBorrowerProfilingProvider";
import { useBorrowerRiskData } from "../contexts/selectors/selectors";
import { useAppDispatch } from "app/hooks";
import { setRiskId } from "features/subscription-process/slices/subscriptionProcessSlice";

const BorrowerProfilingProcess = () => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const navigate = useNavigate();

  const { borrowerProjectId } = useParams<{ borrowerProjectId: string }>();
  const riskData = useBorrowerRiskData()
  const insureds = riskData?.insureds
  const loans = riskData?.loans

  const dispatch = useAppDispatch();

  // Synchronise le riskId Redux avec le projet de l'URL : plusieurs formulaires emprunteur
  // (prêt, assuré...) lisent `riskId` comme borrowerProjectId. Sans ça, après un changement
  // de simulation, ils enverraient les données au MAUVAIS projet (id périmé).
  useEffect(() => {
    if (borrowerProjectId) {
      dispatch(setRiskId(borrowerProjectId));
    }
  }, [dispatch, borrowerProjectId]);

  const isStepValid = useMemo(() => {
    if (insureds && loans) {
      return loans.length > 0 && insureds.length > 0
    }
    return false
  }, [insureds, loans])

  const handleNextStep = useCallback(async () => {
    if (!insureds || !loans || !borrowerProjectId) {
      return
    }
    navigate(`/simulation/tarification/emprunteur/${borrowerProjectId}/${insureds[0].borrowerId}`);
  }, [navigate, insureds, loans, borrowerProjectId])

  const stepPageConfig = useMemo(() => ({
    nextConfig: {
      label: t('COMMON.NEXT_STEP'),
      disabled: !isStepValid,
      onClick: handleNextStep
    },
    containerSize: Sizes["5xl"],
  }), [t, isStepValid, handleNextStep]);

  return (
    <StepPage {...stepPageConfig}>
      <div className="flex flex-col gap-4">
        <BorrowerProfilingProjectSection />
        <BorrowerProfilingInsuredSection />
        {borrowerProjectId && <BorrowerProjectDocumentsSection borrowerProjectId={borrowerProjectId} />}
        <BorrowerProfilingLoanSection />
      </div>
    </StepPage>
  );
};

const BorrowerProfilingProcessWrapper = () => {
  return <MainBorrowerProfilingProvider>
    <BorrowerProfilingProcess />
  </MainBorrowerProfilingProvider>
}

export default BorrowerProfilingProcessWrapper
